import { brandConfig } from '../../../config/brands';
export const SCENARIO_COUNT_OPTIONS = [1, 2, 3, 4, 5];

// ナレーション (TTS) プロバイダのコード一覧。backend registry.NARRATION_PROVIDERS と一致させる。
export const NARRATION_PROVIDERS = ['elevenlabs', 'gemini', 'fish'] as const;
export type NarrationProviderCode = (typeof NARRATION_PROVIDERS)[number];

/**
 * ナレーションの「読み方」(スタイル・話者ごとに voice_map.style で持つ)。backend fish_audio_client.NARRATION_STYLE_TAGS_S2 の key と一致させる。
 * Fish Audio (S2 系の制御タグ)・ElevenLabs (v4 の音声タグ) は各シーン文頭にタグを付け、Gemini 3.8 は別引数で渡す。'' は指定なし。
 */
export const NARRATION_STYLE_CUSTOM = 'custom';

export const NARRATION_STYLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'トーン' },
  { value: 'calm', label: '落ち着いたナレーション' },
  { value: 'excited', label: '興奮して' },
  { value: 'fast', label: '早口で' },
  { value: 'slow', label: 'ゆっくりと' },
  { value: 'loud', label: '大声で' },
  { value: 'quiet', label: '小声で' },
  { value: 'casual', label: '友だちとしゃべる' },
  { value: 'formal', label: 'フォーマルに' },
  // 自由記述。選ぶとインラインの入力欄が出て、その文をタグ (backend で無害化) として送る。
  // 表示側で直前に区切り線を挟む (SingleSelectDropdown の divider は独立した区切り行)
  { value: NARRATION_STYLE_CUSTOM, label: 'カスタム入力...' },
];

/**
 * 読み方 (スタイル) 指定に対応するプロバイダの予備値。
 * 実際の出し分けは BE の GET /generation/narration-capabilities (useNarrationCapabilities) で行う。
 * Gemini は 3.8 経路なら対応・3.1 旧経路なら非対応と BE の設定で変わるため、ここでは取得前・
 * 取得失敗時に表示しない側 (false) に倒しておく。
 */
export const NARRATION_STYLE_SUPPORTED: Record<NarrationProviderCode, boolean> = {
  elevenlabs: true,
  gemini: false,
  fish: true,
};

/** プロバイダ切替セグメントの選択肢 (VoiceSelect / 一括生成モーダルで共用) */
export const NARRATION_PROVIDER_OPTIONS: ReadonlyArray<{ value: NarrationProviderCode; label: string; tooltip: string }> = [
  { value: 'gemini', label: 'Gemini', tooltip: 'Gemini のプリセット音声で生成' },
  { value: 'elevenlabs', label: 'ElevenLabs', tooltip: '日本語声優を含む高品質ボイス' },
  { value: 'fish', label: 'Fish Audio', tooltip: 'Fish Audio の日本語ボイスライブラリで生成 (複数シーン一括生成に対応)' },
];

/** プロバイダ切替タブのアイコン (SegmentedTabs の iconMaskUrl。単色マスクとして使う) */
export const NARRATION_PROVIDER_ICON_MASKS: Record<NarrationProviderCode, string> = {
  elevenlabs: '/logos/elevenlabs-symbol.svg',
  gemini: '/logos/google_gemini_icon.svg',
  fish: '/logos/fish_audio_icon.svg',
};

/** プロバイダの表示名 (履歴・料金表など) */
export const NARRATION_PROVIDER_LABELS: Record<NarrationProviderCode, string> = {
  elevenlabs: 'ElevenLabs',
  gemini: 'Gemini',
  fish: 'Fish Audio',
};

/** BGM 履歴行のモデルファミリー表示名 (BGM セクションの「作曲AIモデル」タブ表記と揃える)。
 *  Lyria Clip / Pro はどちらも Gemini 系として出す。 */
export const BGM_PROVIDER_LABELS: Record<string, string> = {
  elevenlabs: 'ElevenLabs',
  lyria: 'Gemini',
  'lyria-pro': 'Gemini',
};

/** BGM 履歴行のモデルファミリー名。未記録 (アップロード素材) は undefined。 */
export function bgmProviderLabel(provider?: string | null): string | undefined {
  if (!provider) return undefined;
  return BGM_PROVIDER_LABELS[provider] ?? provider;
}

/** provider → credit_rate_config.resource_type (backend NARRATION_RESOURCE_TYPE_BY_PROVIDER と一致) */
export const NARRATION_RESOURCE_TYPE_BY_PROVIDER: Record<NarrationProviderCode, string> = {
  elevenlabs: 'elevenlabs-tts',
  gemini: 'gemini-tts',
  fish: 'fish-tts',
};

/**
 * ボイスの gender / age ラベルを持ち、フィルタと推奨ナレーター絞り込みが効くプロバイダの予備値。
 * Fish Audio は公開ライブラリのタグ (Male / Female / Young / Middle Aged 等) を BE が
 * labels.gender / age に写す。Gemini は 3.8 経路なら拡張ボイスライブラリの声 (性別・年代付き) で
 * 絞り込めるが、BE の capabilities.voice_filters を取得する前・旧 BE では非対応に倒す。
 * 画面の出し分けは useVoiceFiltersSupported (hooks/useNarrationCapabilities) を使う。
 */
export function providerSupportsVoiceFilters(provider: NarrationProviderCode): boolean {
  return provider !== 'gemini';
}

/**
 * 性別 / 年代 (Fish は権利処理済みも) の絞り込みを API 側で行うプロバイダ。
 * Fish Audio の公開ライブラリ・ElevenLabs の共有ライブラリはどちらも上位 100 件しか取れないため、
 * 取得後に絞ると「女性×年配」等が数件しか残らない。絞り込み条件ごとに一覧を取り直す。
 * Gemini はプリセット音声 (全件・ラベル無し) で対象外。
 */
export function providerFiltersVoicesOnServer(provider: NarrationProviderCode): boolean {
  return provider !== 'gemini';
}

/**
 * 「権利処理済み」チェックボックスを出すプロバイダ。Fish Audio は Fish Official が権利処理して
 * 提供するボイス (licensed) と、コミュニティ投稿の声 (有名人の無断クローン等が混在) が同じ
 * ライブラリに並ぶため、前者だけに絞れるようにする。既定はオフ。並び順には影響しない。
 */
export function providerSupportsLicensedFilter(provider: NarrationProviderCode): boolean {
  return provider === 'fish';
}

/**
 * 「権利処理済み」フィルターの既定値。ブランド設定 (features.fishLicensedVoicesDefault) が
 * オンのブランド (SAITE) では Fish Audio を既定で権利処理済みのみにする。
 * VoiceSelect の初期状態と、一括生成の自動選択が参照する一覧 (useNarrationHandlers) で共有する。
 */
export function defaultLicensedVoiceFilter(provider: NarrationProviderCode): boolean {
  return providerSupportsLicensedFilter(provider) && !!brandConfig.features.fishLicensedVoicesDefault;
}

/**
 * API 側で絞るプロバイダのフィルタ選択肢。無条件一覧 (上位 100 件) から動的に抽出すると
 * 該当が少ない値 (中性的 / 年配) のチップが出ないことがあるため固定する。
 * backend narration.VOICE_FILTER_GENDERS / VOICE_FILTER_AGES と一致させる。
 */
export const SERVER_VOICE_FILTER_OPTIONS = {
  genders: ['male', 'female', 'neutral'],
  // 年代は 2 区分。「中高年」(middle_aged) は BE が各プロバイダの middle_aged + old を合算する
  // ('old' 単独は該当が少ないため区分から外した)
  ages: ['young', 'middle_aged'],
} as const;

/**
 * ナレーターの絞り込みの選択肢の並び順 (全プロバイダ共通)。ここに無い値 (ElevenLabs の old・
 * 未知の地域等) は、並びの後ろに一覧の出現順で続ける。
 */
export const VOICE_FILTER_OPTION_ORDER = {
  genders: ['male', 'female', 'neutral'],
  ages: ['young', 'middle_aged'],
  accents: ['標準語', '関西弁', '博多弁'],
} as const;

/**
 * 地域の「標準語」の表示名 (Gemini の labels.accent)。backend はプリセットの声にも付ける。
 * 方言の指定が無いシナリオでは、ナレーションの声の一覧をこの地域で絞った状態から始める
 */
export const STANDARD_VOICE_ACCENT_LABEL = VOICE_FILTER_OPTION_ORDER.accents[0];

/** options を order の順に並べる (order に無い値は元の順のまま後ろへ) */
export function sortByPreferredOrder(options: string[], order: readonly string[]): string[] {
  const rank = (v: string) => {
    const i = order.indexOf(v);
    return i === -1 ? order.length : i;
  };
  return options
    .map((v, i) => ({ v, i }))
    .sort((a, b) => rank(a.v) - rank(b.v) || a.i - b.i)
    .map(({ v }) => v);
}

/**
 * ボイスの年代ラベルを UI の 2 区分に寄せる ('old' は「中高年」に統合)。
 * 推奨ナレーター (AI 出力) や取得済みボイスのラベルには 'old' が残るため、比較前に揃える。
 */
export function normalizeVoiceAge(age: string | undefined | null): string | undefined {
  if (!age) return undefined;
  return age === 'old' ? 'middle_aged' : age;
}

// 画像添付数の制限（APIごとに異なる）
export const MAX_REFERENCE_IMAGES_FOR_SCENE = 20; // シーン画像生成用
export const MAX_REFERENCE_IMAGES = MAX_REFERENCE_IMAGES_FOR_SCENE; // デフォルトはシーン画像生成用
export const MAX_REFERENCE_IMAGES_FOR_VIDEO = 3; // Veo 3.1 referenceImages上限（AI俳優含む）

// Seedance (BytePlus) の参照画像下限 (短辺px)。backend/services/scenario/clients/video_providers/
// seedance_provider.py の min_reference_image_short_side と同じ値。これ未満は backend が
// 「2 倍までの拡大 + 余白」で 300px に補正して送る (reference-image-upscale-plan.md・2026-11-05)
export const SEEDANCE_MIN_REFERENCE_IMAGE_SHORT_SIDE = 300;
// 補正できる下限 (短辺px)。seedance_provider.py の reference_image_upscale_floor と同じ値。これ未満の画像だけ
// 送信対象から除外し、グレーアウトする (300px 未満でもこれ以上なら送る。backend が補正する)
export const SEEDANCE_REFERENCE_IMAGE_UPSCALE_FLOOR = 32;

export const STORAGE_KEY_PRODUCT_NAME = 'cm_scenario_product_name';
export const STORAGE_KEY_BRAND_NAME = 'cm_scenario_brand_name';
export const STORAGE_KEY_AUTO_GENERATE_FIRST_FRAME = 'cm_scenario_auto_generate_first_frame';
export const STORAGE_KEY_AUTO_GENERATE_ENDCARD_FRAME = 'cm_scenario_auto_generate_endcard_frame';
export const STORAGE_KEY_AUTO_GENERATE_NARRATION = 'cm_scenario_auto_generate_narration';
export const STORAGE_KEY_AUTO_GENERATE_STORYBOARD = 'cm_scenario_auto_generate_storyboard';
export const STORAGE_KEY_AUTO_APPEND_TO_PREVIOUS_SCENE = 'cm_scenario_auto_append_to_previous_scene';
export const STORAGE_KEY_SELECTED_ASIN = 'cm_scenario_selected_asin';
export const STORAGE_KEY_SCENARIO_COUNT = 'cm_scenario_count';
export const STORAGE_KEY_GENERATION_MODEL = 'cm_scenario_generation_model';
// 思考レベルのキーは modelCatalog 側 (v2 へ移行済み・旧キーからの引き継ぎ込み) を正本にする
export { STORAGE_KEY_THINKING_LEVEL } from '../state/modelCatalog';
export const STORAGE_KEY_SCENARIO_MODEL = 'cm_scenario_ai_model';
export const STORAGE_KEY_TONE = 'cm_scenario_tone';
/** 想定尺。プルダウンでの明示選択時のみ保存する (歌モノ補正・推奨など programmatic な変更は保存しない)。 */
export const STORAGE_KEY_DURATION = 'cm_scenario_duration';
export const STORAGE_KEY_SKIP_NO_FRAME_WARNING = 'cm_scenario_skip_no_frame_warning';
export const STORAGE_KEY_INCLUDE_NARRATION = 'cm_scenario_include_narration';
export const STORAGE_KEY_INCLUDE_TELOP = 'cm_scenario_include_telop';
export const STORAGE_KEY_INCLUDE_DIALOGUE = 'cm_scenario_include_dialogue';
export const STORAGE_KEY_EXCLUDE_PEOPLE = 'cm_scenario_exclude_people';
// ストーリーボードモードで音声ありのとき表示する「BGMおまかせ／なし」トグルの最終状態。
// シナリオ横断で前回の選択を既定にするためグローバル。未保存時の既定は「BGMなし」
// (保存値が '1' のときだけ「BGMおまかせ」)。
export const STORAGE_KEY_STORYBOARD_INCLUDE_BGM = 'cm_storyboard_include_bgm';
export const STORAGE_KEY_PLAYED_VIDEOS = 'cm_scenario_played_videos';
export const STORAGE_KEY_PLAYED_NARRATIONS = 'cm_scenario_played_narrations';
export const SCENARIO_JOB_STORAGE_KEY = 'cm_scenario_pending_job';
// シナリオ生成 mutation の識別キー (useScenarioGeneration の復帰 effect が
// queryClient.isMutating で「進行中の生成」を検知し二重ポーリングを避けるために使う)。
export const SCENARIO_GENERATE_MUTATION_KEY = ['scenario-generate'] as const;
